import { useState, type ReactNode } from 'react';
import { ChevronDown, LoaderCircle, UserRound, X } from 'lucide-react';

import {
  isPostalFilter,
  MIN_RECIPIENT_QUERY_LENGTH,
  postalSearchDefaults,
  recipientDeliveryLabel,
  recipientLocation,
  recipientName,
  REFERRAL_CHANNEL_LABELS,
  useNppesRecipientSearch,
  type ReferralRecipient,
} from '@/entities/referral-recipient';
import { useUserInfoQuery } from '@/entities/user-info';
import { useDebouncedValue } from '@/shared/lib/useDebouncedValue';
import { Button } from '@/shared/ui/shadcn/button';
import { Command, CommandInput, CommandItem, CommandList } from '@/shared/ui/shadcn/command';
import { Input } from '@/shared/ui/shadcn/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/shadcn/popover';

const SEARCH_DEBOUNCE_MS = 300;

interface RecipientSearchPopoverProps {
  patientId: string;
  recipient: ReferralRecipient | null;
  onSelect(recipient: ReferralRecipient): void;
  onClear(): void;
}

/** The referral's To field: shows the chosen recipient and searches for a new one. */
export function RecipientSearchPopover({
  patientId,
  recipient,
  onSelect,
  onClear,
}: RecipientSearchPopoverProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // ZIP and state default to the patient's address until the user edits them.
  const [postalCodeEdit, setPostalCode] = useState<string | null>(null);
  const [stateEdit, setState] = useState<string | null>(null);
  const debouncedQuery = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const patientAddress = useUserInfoQuery(patientId).data?.patientInfo?.address;
  const defaults = postalSearchDefaults(patientAddress);
  const postalCode = postalCodeEdit ?? defaults.postalCode;
  const state = stateEdit ?? defaults.state;

  const select = (next: ReferralRecipient) => {
    onSelect(next);
    setOpen(false);
  };

  return (
    <div className="flex min-w-0 flex-1 items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md border px-3 text-left text-sm hover:bg-muted/50 data-[state=open]:border-ring"
            aria-label={
              recipient ? `Change recipient ${recipientName(recipient)}` : 'Choose recipient'
            }
          >
            {recipient ? (
              <>
                <span className="truncate font-medium">{recipientName(recipient)}</span>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {REFERRAL_CHANNEL_LABELS[recipient.channel]} · {recipientDeliveryLabel(recipient)}
                </span>
              </>
            ) : (
              <>
                <UserRound className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="text-muted-foreground">Choose recipient…</span>
              </>
            )}
            <ChevronDown
              className="ml-auto size-3.5 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[min(32rem,calc(100vw-2rem))] p-0">
          <Command shouldFilter={false}>
            <CommandInput
              value={query}
              onValueChange={setQuery}
              placeholder="Search name, practice, or NPI…"
              aria-label="Search recipients"
            />
            <div className="flex gap-2 border-b px-3 py-2">
              <Input
                value={postalCode}
                onChange={(event) =>
                  setPostalCode(event.target.value.replace(/[^\d*]/g, '').slice(0, 6))
                }
                placeholder="ZIP or 911*"
                aria-label="ZIP code"
                className="h-8 w-28"
              />
              <Input
                value={state}
                onChange={(event) =>
                  setState(
                    event.target.value
                      .replace(/[^a-z]/gi, '')
                      .slice(0, 2)
                      .toUpperCase()
                  )
                }
                placeholder="e.g. CA"
                aria-label="State"
                className="h-8 w-20"
              />
            </div>
            <CommandList className="max-h-80">
              <NppesResults
                query={debouncedQuery}
                postalCode={postalCode}
                state={state}
                onSelect={select}
              />
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {recipient ? (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="size-8 shrink-0"
          onClick={onClear}
          aria-label="Clear recipient"
        >
          <X className="size-3.5" />
        </Button>
      ) : null}
    </div>
  );
}

function SearchStatus({ children }: { children: ReactNode }) {
  return (
    <p className="flex items-center justify-center gap-2 px-3 py-6 text-center text-sm text-muted-foreground">
      {children}
    </p>
  );
}

function PostalFilterHint() {
  return <SearchStatus>Use a ZIP or at least two ZIP digits, e.g. 911*.</SearchStatus>;
}

function RecipientOption({
  recipient,
  detail,
  onSelect,
}: {
  recipient: ReferralRecipient;
  detail: string;
  onSelect(recipient: ReferralRecipient): void;
}) {
  const location = recipientLocation(recipient);
  return (
    <CommandItem
      value={`${recipient.channel}:${recipient.destination.npi}:${location}`}
      onSelect={() => onSelect(recipient)}
      className="flex-col items-start gap-0.5 px-3 py-2"
    >
      <span className="font-medium">{recipientName(recipient)}</span>
      <span className="text-xs text-muted-foreground">
        {[detail, location, `NPI ${recipient.destination.npi}`].filter(Boolean).join(' · ')}
      </span>
    </CommandItem>
  );
}

function NppesResults({
  query,
  postalCode,
  state,
  onSelect,
}: {
  query: string;
  postalCode: string;
  state: string;
  onSelect(recipient: ReferralRecipient): void;
}) {
  const search = useNppesRecipientSearch({ query, postalCode, state });
  if (query.length < MIN_RECIPIENT_QUERY_LENGTH) {
    return <SearchStatus>Search by provider, practice, or NPI.</SearchStatus>;
  }
  if (!isPostalFilter(postalCode)) {
    return <PostalFilterHint />;
  }
  if (search.isPending) {
    return (
      <SearchStatus>
        <LoaderCircle className="size-4 animate-spin" /> Searching…
      </SearchStatus>
    );
  }
  if (search.isError) {
    return <SearchStatus>Search failed. Try again.</SearchStatus>;
  }

  if (search.data.type === 'unavailable') {
    return <SearchStatus>{search.data.details}</SearchStatus>;
  }
  const recipients = search.data.recipients;
  if (recipients.length === 0) {
    return (
      <SearchStatus>
        {postalCode ? `No NPPES matches near ${postalCode}.` : 'No NPPES matches.'}
      </SearchStatus>
    );
  }

  // The closest matches come back as one ordered list, so there is no paging.
  return recipients.map((recipient) => (
    <RecipientOption
      key={`${recipient.destination.npi}:${recipient.destination.practice_location.fax_number}:${recipientLocation(recipient)}`}
      recipient={recipient}
      detail={recipient.destination.specialties[0] ?? ''}
      onSelect={onSelect}
    />
  ));
}
